/** Connecting a Telegram chat from the student's own device (STORY-122, FR-052, FR-058).
 *
 * One affordance for the student's live screen (a guest's only surface) and the settings
 * page: the disclosure is shown, then a code is asked for with `disclosed: true`, then the
 * deep link is opened through `capabilities.opener` (never `window.open`: the desktop webview
 * does not implement it). The code and the link are rendered here and nowhere else: not on a
 * staff screen, not on the projector.
 *
 * A guest passes the guest token it got when it joined; a signed-in account passes nothing
 * and the api client's bearer is used. Nothing is rendered when the bot is off.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { serviceUrl } from "../api/base";
import { api, ApiError } from "../api/client";
import { capabilities } from "../platform/capabilities";
import { copyText } from "../platform/clipboard";
import { Button, ExternalLink, useConfirm } from "../ui";
import { DISCLOSURE_VERSION, Disclosure, EndingAfter, EndingBefore } from "./Disclosure";

export type TelegramStatus = { linked: boolean; linked_at: string | null; notify_join: boolean; notify_reports: boolean; disclosure_version?: string | null };
type BotInfo = { enabled: boolean; username: string | null };
type LinkOut = { code: string; url: string; expires_in_s: number };
/** What the erasure did (STORY-127): counts only. */
type EraseOut = { deleted: Record<string, number>; messages_deleted: number; messages_out_of_reach: number; messages_failed: Record<string, number>; third_party_residue: string };
const failedCount = (r: EraseOut) => Object.values(r.messages_failed ?? {}).reduce((a, b) => a + b, 0);

/** One call to the integrations service as the given principal: a guest's token when there
 * is one (the api client only knows the signed-in account), else the account's bearer. */
export async function telegramCall<T>(method: string, path: string, token: string | null | undefined, body?: unknown): Promise<T> {
  const init: RequestInit = { method, body: body === undefined ? undefined : JSON.stringify(body) };
  if (!token) return api<T>(path, init);
  const res = await fetch(serviceUrl(path), { ...init, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } });
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const b = (parsed ?? {}) as { detail?: string; code?: string };
    throw new ApiError(res.status, typeof b.detail === "string" ? b.detail : `HTTP ${res.status}`, b.code, parsed);
  }
  return parsed as T;
}

type Props = {
  /** The guest principal; absent for a signed-in account. */
  token?: string | null;
  /** Labels the section for assistive technology; the course setting links to `me-telegram`. */
  id: string;
  title: string;
  lede: string;
  /** What to show once a chat is linked (preferences, disconnect); the settings page's. */
  linked?: (status: TelegramStatus, refetch: () => void) => ReactNode;
};

export function ConnectCard({ token, id, title, lede, linked }: Props) {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const who = token ?? "account";
  const bot = useQuery({ queryKey: ["telegram", "bot", who], queryFn: () => telegramCall<BotInfo>("GET", "/integrations/v1/telegram/bot", token), retry: false });
  const [link, setLink] = useState<LinkOut | null>(null);
  const status = useQuery({
    queryKey: ["telegram", "me", who],
    queryFn: () => telegramCall<TelegramStatus>("GET", "/integrations/v1/me/telegram", token),
    enabled: !!bot.data?.enabled,
    retry: false,
    // while a code is out, watch for the bot's /start to land
    refetchInterval: (q) => (link && q.state.data && !q.state.data.linked ? 4000 : false),
  });
  const [step, setStep] = useState<"idle" | "reading">("idle");
  // Whether the fallback message reached the clipboard; `false` leaves the text as it is,
  // shown, never a "Copied" that did not happen.
  const [copied, setCopied] = useState(false);
  // STORY-127: the boundary of the erasure, stated after it. Counts when this screen did
  // the erasing; the statement alone when the link ended elsewhere (the settings page's
  // own button, /stop in the chat) while this card was watching.
  const [ended, setEnded] = useState<EraseOut | "elsewhere" | null>(null);
  const wasLinked = useRef(false);
  useEffect(() => {
    const linkedNow = status.data?.linked === true;
    if (wasLinked.current && !linkedNow) setEnded((e) => e ?? "elsewhere");
    wasLinked.current = linkedNow;
  }, [status.data?.linked]);
  const connect = useMutation({
    mutationFn: () => telegramCall<LinkOut>("POST", "/integrations/v1/me/telegram/link", token, { disclosed: true, version: DISCLOSURE_VERSION }),
    onSuccess: (l) => {
      setLink(l);
      setStep("idle");
      setEnded(null);
      setCopied(false);
      void capabilities.opener.open(l.url);
    },
  });
  const erase = useMutation({
    mutationFn: () => telegramCall<EraseOut>("POST", "/integrations/v1/me/telegram/erase", token),
    onSuccess: (r) => {
      setEnded(r);
      setLink(null);
      void status.refetch();
    },
  });
  // Ending it is irreversible (our records and the chat's last 48 hours go together), so it
  // asks once, with the same sentence the card shows beside the action; focus lands on
  // Cancel (`destructive`), a hurried Return keeps the link.
  const disconnect = async () => {
    if (await confirm({ title: t("tg.disconnectAsk"), body: t("tg.disclosure.ending.before"), confirmLabel: t("tg.disconnect"), destructive: true })) erase.mutate();
  };
  if (!bot.data?.enabled) return null;
  const s = status.data;
  const err = connect.error instanceof ApiError ? connect.error : null;
  const refuse = err?.code === "no_identity" ? t("tg.noIdentity") : err?.code === "bot_off" ? t("tg.botOff") : err ? t("tg.failed") : null;
  return (
    <section className="rounded-[var(--radius-card)] bg-[color:var(--surface)] p-4 text-sm text-[color:var(--ink)] shadow-sm" aria-labelledby={id}>
      <h2 id={id} className="mb-1 font-semibold">{title}</h2>
      <p className="mb-3 text-[color:var(--ink-2)]">{lede}</p>
      {s?.linked && (
        <div className="flex flex-col items-start gap-2">
          <p role="status">{t("tg.linked")}</p>
          {linked?.(s, () => void status.refetch())}
          <EndingBefore />
          <Button variant="destructive" loading={erase.isPending} onClick={() => void disconnect()}>{t("tg.disconnect")}</Button>
          {erase.isError && <p role="alert" style={{ color: "var(--danger)" }}>{t("tg.disconnectFailed")}</p>}
        </div>
      )}
      {s && !s.linked && ended && step === "idle" && !link && (
        ended === "elsewhere" ? <EndingAfter /> : <EndingAfter deleted={ended.messages_deleted} outOfReach={ended.messages_out_of_reach} failed={failedCount(ended)} />
      )}
      {s && !s.linked && step === "idle" && !link && (
        <Button variant="secondary" onClick={() => setStep("reading")}>{t("tg.connect")}</Button>
      )}
      {s && !s.linked && step === "reading" && (
        <div className="flex flex-col gap-4">
          <Disclosure />
          <div className="flex flex-wrap gap-2">
            <Button loading={connect.isPending} onClick={() => connect.mutate()}>{t("tg.agree")}</Button>
            <Button variant="secondary" onClick={() => setStep("idle")}>{t("tg.notNow")}</Button>
          </div>
        </div>
      )}
      {s && !s.linked && link && (
        <div className="flex flex-col gap-2">
          {/* The one action of this state is a button, not a text link; the message beneath it
              is the fallback for a phone that cannot deep-link, with a copy control so a code
              with an underscore and a hyphen is never long-pressed into the clipboard. */}
          <p>
            <ExternalLink className="inline-flex min-h-[var(--control-h)] items-center justify-center rounded-[var(--radius-control)] bg-[color:var(--accent)] px-5 py-2 text-base font-semibold text-[color:var(--accent-ink)] no-underline hover:brightness-95" href={link.url}>
              {t("tg.open")}
            </ExternalLink>
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>{t("tg.code")}</span>
            <code className="num select-all" translate="no">{`/start ${link.code}`}</code>
            <Button variant="tertiary" onClick={() => void copyText(`/start ${link.code}`).then(setCopied)}>{t("tg.copy")}</Button>
            {copied && <span role="status" className="text-xs text-[color:var(--ink-2)]">{t("tg.copied")}</span>}
          </p>
          <p className="text-xs text-[color:var(--ink-2)]">{t("tg.codeHint")}</p>
          <p role="status" className="text-xs text-[color:var(--ink-2)]">{t("tg.waiting")}</p>
        </div>
      )}
      {refuse && <p role="alert" className="mt-2" style={{ color: "var(--danger)" }}>{refuse}</p>}
      {step !== "reading" && (
        <details className="mt-3 text-[color:var(--ink-2)]">
          <summary className="flex min-h-[var(--control-h)] cursor-pointer items-center leading-snug underline">{t("tg.details")}</summary>
          <div className="mt-2 text-[color:var(--ink)]"><Disclosure /></div>
        </details>
      )}
    </section>
  );
}
